import { ActionIcon, Loader, Menu } from '@mantine/core';
import {
    IconDots,
    IconFolderShare,
    IconPencil,
    IconPlayerPause,
    IconPlayerPlay,
    IconPlugConnected,
    IconRotateClockwise2,
    IconSquare,
    IconTerminal2,
    IconTrash,
} from '@tabler/icons-react';
import { moveTriggerProps } from '@xver/hooks/useMoveResource';
import { useTranslation } from 'react-i18next';

import { InstanceResponseModel } from '../../../../../../../apis/v1/instances/model';

interface InstanceActionsMenuProps {
    instance: InstanceResponseModel;
    isLoading: boolean;
    /** False with view-only access: only "How to connect" is offered. */
    canManage: boolean;
    canMove: boolean;
    onHowToConnect: () => void;
    onOpenConsole: () => void;
    onToggleBackup: () => void;
    onEditProfile: () => void;
    onMove: () => void;
    onReboot: () => void;
    onPowerOff: () => void;
    onPowerOn: () => void;
    onDestroy: () => void;
}

export default function InstanceActionsMenu({
    instance,
    isLoading,
    canManage,
    canMove,
    onHowToConnect,
    onOpenConsole,
    onToggleBackup,
    onEditProfile,
    onMove,
    onReboot,
    onPowerOff,
    onPowerOn,
    onDestroy,
}: InstanceActionsMenuProps) {
    const { t } = useTranslation('cloudCompute');
    const isRunning = instance.state.power === 'running';
    const isStopped = instance.state.power === 'stopped';

    if (!isRunning && !isStopped) return null;

    return (
        <Menu withArrow position="right">
            <Menu.Target>
                <ActionIcon
                    {...moveTriggerProps('instance', instance.id)}
                    variant="transparent"
                    aria-label={t('table.instanceActions')}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isLoading}
                    style={{ backgroundColor: 'transparent' }}
                >
                    {isLoading ? (
                        <Loader color="var(--fg)" size={20} />
                    ) : (
                        <IconDots size="20" className="text-fg" />
                    )}
                </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Item
                    disabled={!isRunning}
                    leftSection={<IconPlugConnected size={20} stroke={1.5} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        onHowToConnect();
                    }}
                >
                    {t('menu.howToConnect')}
                </Menu.Item>

                {canManage && (
                    <>
                        <Menu.Item
                            leftSection={<IconTerminal2 size={20} stroke={1.5} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenConsole();
                            }}
                        >
                            {t('menu.webConsole')}
                        </Menu.Item>

                        {instance.state.backup === 'enabled' ? (
                            <Menu.Item
                                leftSection={<IconPlayerPause size={20} stroke={1.5} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleBackup();
                                }}
                            >
                                {t('menu.disableBackups')}
                            </Menu.Item>
                        ) : (
                            <Menu.Item
                                leftSection={<IconPlayerPlay size={20} stroke={1.5} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleBackup();
                                }}
                            >
                                {t('menu.enableBackups')}
                            </Menu.Item>
                        )}

                        <Menu.Item
                            leftSection={<IconPencil size={20} stroke={1.5} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditProfile();
                            }}
                        >
                            {t('menu.editProfile')}
                        </Menu.Item>

                        {canMove && (
                            <Menu.Item
                                leftSection={<IconFolderShare size={20} stroke={1.5} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMove();
                                }}
                            >
                                {t('projects:resource.moveToProject')}
                            </Menu.Item>
                        )}

                        <Menu.Divider />

                        <Menu.Item
                            disabled={!isRunning}
                            color="var(--color-status-warning)"
                            leftSection={<IconRotateClockwise2 size={20} stroke={1.5} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onReboot();
                            }}
                        >
                            {t('menu.reboot')}
                        </Menu.Item>

                        {isRunning ? (
                            <Menu.Item
                                color="var(--color-status-danger)"
                                leftSection={<IconSquare size={20} stroke={1.5} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPowerOff();
                                }}
                            >
                                {t('menu.stop')}
                            </Menu.Item>
                        ) : (
                            <Menu.Item
                                color="var(--color-status-success)"
                                leftSection={<IconPlayerPlay size={20} stroke={1.5} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPowerOn();
                                }}
                            >
                                {t('menu.start')}
                            </Menu.Item>
                        )}

                        <Menu.Item
                            color="var(--color-status-danger)"
                            leftSection={<IconTrash size={20} stroke={1.5} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDestroy();
                            }}
                        >
                            {t('menu.destroy')}
                        </Menu.Item>
                    </>
                )}
            </Menu.Dropdown>
        </Menu>
    );
}
